import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Handshake, Sparkles, Users, Trophy, ChevronRight, Loader2 } from 'lucide-react';
import { api } from '../lib/api';

export default function TopTraders() {
  const [state, setState] = useState({ loading: true, list: [], error: null });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await api.getTopTraders(12);
        if (!cancelled) setState({ loading: false, list: list || [], error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, list: [], error: e.message || 'error' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state.error || (!state.loading && state.list.length === 0)) return null;

  const anyTraders = state.list.some((t) => t.kind === 'trader');

  return (
    <section
      className="max-w-7xl mx-auto px-3 sm:px-6 mt-4 sm:mt-6"
      data-testid="top-traders-section"
    >
      <div
        className="rounded-2xl border-[3px] border-[#3a1064] shadow-[0_5px_0_#3a1064] overflow-hidden"
        style={{ background: 'var(--t-card)' }}
      >
        {/* Header */}
        <div
          className="p-3 sm:p-4 flex items-center gap-2 flex-wrap"
          style={{
            background:
              'linear-gradient(90deg,#ffd900 0%,#ff2bd6 55%,#00b8ff 100%)',
          }}
        >
          <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-white border-2 border-[#3a1064] flex items-center justify-center shrink-0 shadow-[0_2px_0_#3a1064]">
            {anyTraders
              ? <Trophy className="h-5 w-5 text-[#ff2bd6]" strokeWidth={2.5} />
              : <Users className="h-5 w-5 text-[#ff2bd6]" strokeWidth={2.5} />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] sm:text-xs font-black uppercase tracking-[2px] text-[#3a1064]/80">
              Find a trader
            </div>
            <div className="font-black text-[#3a1064] text-base sm:text-xl leading-tight">
              {anyTraders ? 'Top Sprite Vault traders' : 'Say hi to our newest players'}
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] font-black text-[#3a1064] bg-white/70 border-2 border-[#3a1064] rounded-full px-2.5 py-1">
            <Sparkles className="h-3 w-3" /> Tap any card to open their trade page
          </div>
        </div>

        {/* Loading */}
        {state.loading && (
          <div className="p-6 text-center">
            <Loader2 className="h-5 w-5 mx-auto animate-spin text-[#ff2bd6]" />
          </div>
        )}

        {/* Cards row — horizontally scrollable on mobile */}
        {!state.loading && state.list.length > 0 && (
          <div className="p-3 sm:p-4">
            <div className="flex gap-2.5 sm:gap-3 overflow-x-auto snap-x snap-mandatory pb-1 -mx-1 px-1">
              {state.list.map((t, idx) => (
                <TraderCard
                  key={t.id}
                  trader={t}
                  rank={anyTraders && t.kind === 'trader' ? idx + 1 : null}
                  onOpen={() => navigate(`/u/${t.username}`)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function TraderCard({ trader, rank, onOpen }) {
  const initial = ((trader.name || trader.username || '?')[0] || '?').toUpperCase();
  const isTrader = trader.kind === 'trader';
  const medalBg = rank === 1 ? '#ffd900' : rank === 2 ? '#d6d6e6' : rank === 3 ? '#cd7f32' : '#ffffff';

  return (
    <button
      data-testid={`top-trader-card-${trader.username}`}
      onClick={onOpen}
      className="snap-start shrink-0 w-[150px] sm:w-[170px] rounded-2xl border-[3px] border-[#3a1064] bg-white p-3 shadow-[0_4px_0_#3a1064] hover:-translate-y-0.5 hover:shadow-[0_6px_0_#3a1064] transition-transform text-left flex flex-col items-center gap-1.5 relative"
    >
      {rank !== null && (
        <div
          className="absolute -top-2 -left-2 h-7 w-7 rounded-full border-2 border-[#3a1064] flex items-center justify-center font-black text-xs text-[#3a1064] shadow-[0_2px_0_#3a1064]"
          style={{ background: medalBg }}
        >
          {rank}
        </div>
      )}
      {trader.role === 'admin' && (
        <div className="absolute -top-2 right-2 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-full bg-yellow-300 text-[#3a1064] border-2 border-[#3a1064]">
          Dev
        </div>
      )}
      <div
        className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl border-[3px] border-[#3a1064] overflow-hidden flex items-center justify-center text-2xl sm:text-3xl font-black text-white shadow-[0_3px_0_#3a1064]"
        style={{
          background:
            'linear-gradient(135deg,#ffd900 0%,#ff2bd6 55%,#9d3dff 100%)',
        }}
      >
        {trader.picture ? (
          <img src={trader.picture} alt={trader.username} className="h-full w-full object-cover" />
        ) : (
          initial
        )}
      </div>
      <div className="font-black text-[#3a1064] text-sm text-center leading-tight max-w-full truncate w-full">
        {trader.name || trader.username}
      </div>
      <div className="text-[10px] font-black text-[#3a1064]/70 truncate w-full text-center">
        @{trader.username}
      </div>
      {isTrader ? (
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-emerald-500 text-white border-2 border-[#0c5132]">
          <Handshake className="h-3 w-3" />
          {trader.trade_count} trade{trader.trade_count === 1 ? '' : 's'}
        </div>
      ) : (
        <div className="mt-1 inline-flex items-center gap-1 text-[10px] font-black uppercase px-2 py-0.5 rounded-full bg-[#00b8ff] text-white border-2 border-[#0070a0]">
          <Sparkles className="h-3 w-3" /> New
        </div>
      )}
      <div className="mt-0.5 inline-flex items-center gap-0.5 text-[10px] font-black text-[#ff2bd6]">
        Trade <ChevronRight className="h-3 w-3" strokeWidth={3} />
      </div>
    </button>
  );
}
